import { ref, type Ref } from 'vue';
import { membersApi } from '@/entities/Branch';
import type { MembersFilter } from '@/entities/Branch';
import {
  downloadBlobFile,
  handleApiError,
  useCustomToast,
  type MemberSearchField,
  type SortOrder,
} from '@/shared/lib';

type UseMembersExportParams = {
  searchTerm: Ref<string>;
  searchFieldId: Ref<MemberSearchField>;
  sort: Ref<string>;
  order: Ref<SortOrder>;
  filter: Ref<MembersFilter>;
};

export const useMembersExport = ({
  searchTerm,
  searchFieldId,
  sort,
  order,
  filter,
}: UseMembersExportParams) => {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const { blob, exportedCount, filename } = await membersApi.exportMembersExcel({
        search: searchTerm.value.trim() || undefined,
        searchField: searchFieldId.value,
        sort: sort.value || undefined,
        order: order.value || undefined,
        positionId: filter.value.positionId,
        departmentId: filter.value.departmentId,
        functionalActivityId: filter.value.functionalActivityId,
      });

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1
          ? '1 membro exportado'
          : `${exportedCount} membros exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, 'Tente novamente.');
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExporting,
  };
};

